import { Bell, BookHeart, FileText, House } from 'lucide-react';
import { Fragment, useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode, type KeyboardEvent, type ChangeEvent, type PointerEvent, type MouseEvent as ReactMouseEvent, type CSSProperties } from 'react';
import type { ConversationEmber, ConversationFeatureCard, ConversationJoinCard, ConversationMessage, ConversationPrecedentItem, ConversationReadItem, CrisisLines, HelpAskAnswer, HelpAskOutcome, AgeCheckAnswer, MessageAttachment, TurnAbout } from '../api';
import { navigate } from '../router';
import { ShareAskCard, type ShareAnswer } from './KnockCard';
import { AgeCheckQuestion } from './AgeCheck';
import { ImagePreview } from './ImagePreview';
import { HelpAsk } from './HelpAsk';
import { InboundRegistrationCTA } from './InboundRegistrationCTA';
import { EmberMascot } from './EmberMascot';
import { PresenceAvatar } from './PresenceAvatar';
import { Link } from './Link';
import { LetterCover } from './LetterCover';
import { PeopleLine } from '../tree/PeopleLine';
import { firstSentence } from '../letter-format';
import { VoiceWave, useVoiceRecorder, type MicUnavailableReason } from './voice-input';
import { track, trackFailed } from '../../../../../shared-web/track';
import { useBrowserLayoutEffect } from '../use-browser-layout-effect';
import { useStaleReloadHold } from '../use-stale-reload-hold';
import { useKeptAcrossReload } from '../use-kept-across-reload';
import './ConversationShell.css';

/** How long Stop ignores taps after it takes the send slot (see stopArmed). */
const STOP_ARM_MS = 1000;

// The field's words, kept across a reload onto a newer deploy (draftKey).
const DRAFT_STORE_PREFIX = 'ember-composer-draft:';

/**
 * A photo the person has picked. It is already uploaded, compressed and
 * moderated by the time it appears in the tray — `media_id` is the receipt the
 * send call quotes — except while `uploading`, which is the only state that
 * carries a local object URL instead of a hosted one.
 */
export interface PendingPhoto {
  key: string;
  previewUrl: string;
  uploading: boolean;
  mediaId: string;
}

/**
 * What the viewport could tell us about the keyboard at this moment.
 *
 * `guessed` is keyboard-viewport.ts rule 5: an Android in-app webview reported
 * no geometry at all, so a phantom inset is standing in for a measurement. It
 * is the state where the composer can end up under the keyboard, and until now
 * nothing recorded that it had happened to anyone.
 */
function keyboardKind(): 'measured' | 'guessed' | 'none' {
  if (typeof document !== 'undefined' && document.body.classList.contains('ember-keyboard-guessed')) return 'guessed';
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  if (vv && window.innerHeight - (vv.height + vv.offsetTop) > 80) return 'measured';
  return 'none';
}

export interface ConversationShellProps {
  messages: ConversationMessage[];
  /** Assistant reply in flight — shows the typing indicator + Stop. */
  typing: boolean;
  /** Composer disabled (finalizing / closed conversation). */
  disabled: boolean;
  /**
   * Send one user message. Resolve false to restore the text into the
   * composer (send failed); true/void keeps it cleared.
   */
  onSend: (text: string, mediaIds: string[], about?: TurnAbout | null) => Promise<boolean>;
  onStop: () => void;
  /**
   * A first sentence already in the field (2026-09-24 拍板, talk-about.ts):
   * the person came in through "Have thoughts about this?" on their letter's
   * or their Ember's page. `about` is what travels with that turn — a chip
   * over the field names it and lets them drop it; the sentence is theirs to
   * change. Read once, when it changes.
   */
  draft?: { text: string; about: TurnAbout | null } | null;
  /**
   * Where the words in the field are kept across a reload onto a newer
   * deploy (stale-bundle-reload): one key per conversation or room. Absent,
   * they are lost with the old page.
   */
  draftKey?: string;
  /**
   * A reply that failed after the person's words were kept (2026-09-21): a
   * line under that turn says so, with Try again — Ember answers what is
   * there. Null: nothing failed.
   */
  retry?: { afterTurn: number; onRetry: () => void; stopped?: boolean; unanswered?: boolean } | null;
  /**
   * The first screen's three ways to begin (2026-09-03), shown until the person
   * has said something. Each is a complete first line in the person's voice,
   * and a tap SENDS it as their first turn — ChatGPT's conversation-starter
   * behaviour (拍板 2026-09-03), not a fill-in the person then has to confirm.
   * Omit to show none (a telling that arrived from a question has the question
   * itself for a prompt).
   */
  starters?: string[];
  /**
   * Photos behind the + button, for every telling — signed-in or visitor.
   * `onPickPhoto` uploads one file and resolves the stored id, or null when
   * the upload was refused — it owns telling the person why.
   */
  photos: {
    pending: PendingPhoto[];
    remaining: number;
    onPickPhoto: (file: File) => Promise<void>;
    onRemovePhoto: (key: string) => void;
    /**
     * The whole batch picked at once (2026-10-19 Riley): the page asks the edge how much room is left, keeps
     * what fits and says once what did not. Without it each file goes to onPickPhoto, up to `remaining`.
     */
    onPickPhotos?: (files: File[]) => void;
    /** The line over the composer when the stretch holds all the photos it can (2026-10-19 Riley); null otherwise. */
    fullLine?: string | null;
    /** The + was tapped (2026-10-19 Riley): the page re-reads the room left while the picker is open. */
    onPickerOpen?: () => void;
  };
  /**
   * Voice input behind the mic button (§4.5): a recorded clip becomes words in
   * the field — this is dictation, not the voice call. `transcribe` resolves
   * the text, or null when it failed (it owns telling the person why); it is
   * told what the words were for and what the field held, for a page that
   * cannot make words yet and keeps the recording instead (Talk before the
   * boxes are ticked); `onMicUnavailable` says the browser gave no microphone.
   */
  voice: {
    transcribe: (audio: Blob, then: 'field' | 'send', typed: string) => Promise<string | null>;
    onMicUnavailable: (reason: MicUnavailableReason) => void;
  };
  /** Conversation id stamped onto the shell's instrumented controls. */
  logConversationId?: string;
  /** The room this thread is (registry match_id) — for rows a room's composer mints. */
  logRoomId?: string;
  /**
   * A thread with people (a room, a 1v1): on a phone the field lets go after a send, so the keyboard goes down and the
   * conversation shows (2026-10-27 founder). Ember's own chat keeps it (2026-09-08).
   */
  releaseAfterSend?: boolean;
  /**
   * The thread as a window (2026-09-25, thread-window.ts): `hasMore` — turns
   * older than `messages[0]` exist on the edge; `onLoadOlder` — asked once
   * when the reader scrolls to the top of what is held, and again after each
   * prepend; `loading` — a reach back on the wire (a quiet line at the head
   * says so). The reader's place is kept across the prepend. Omit for a
   * thread held whole.
   */
  history?: { hasMore: boolean; loading: boolean; onLoadOlder: () => void } | null;
  /**
   * The Embers written from this conversation (2026-09-12), drawn in the
   * thread after the turn each one was written up to — the person reads what
   * their words became where they said them. `emberNote` renders under every
   * card: the page's one line for a guest about keeping it.
   */
  embers?: ConversationEmber[];
  emberNote?: ReactNode;
  /** A tap on an Ember card: the page opens it in place (the page owns how). */
  onOpenEmber?: (ember: ConversationEmber) => void;
  /**
   * The share ask waiting on this person (§4.9, 2026-09-27): Ember found
   * something in an Ember of theirs that could help someone, and asks. The
   * card stands after the turn Ember voiced it in (`voiced_turn`), or after
   * Ember's last turn when it has not been voiced yet; only while it waits.
   */
  shareAsk?: { ember_id: string; title: string; voiced_turn: number | null; onAnswered: (answer: ShareAnswer) => void; onReply: (text: string, answer: ShareAnswer) => Promise<boolean> } | null;
  /** The Embers someone let this person read, given in this conversation (§4.9): a card after the turn that gave each. */
  reads?: ConversationReadItem[];
  /** How it went for others (§4.22): the pages given in this conversation; a card after the reply that gave each. */
  precedents?: ConversationPrecedentItem[];
  /** A guest's yes to a shared Ember or a room (2026-10-11 拍板): the card to join after the reply that answered it, and once they joined for a room, the card that says Ember is looking. */
  joinCards?: ConversationJoinCard[];
  /** The cards for meeting people replies brought (2026-10-26, New friends), each after its reply. */
  featureCards?: ConversationFeatureCard[];
  /**
   * The Embers arriving right now (2026-09-12; several at once since
   * 2026-09-15 — one ceremony for whatever is whole at that moment): while
   * it plays their cards stand in the thread invisible, holding their place
   * for the page to fly to; once landed the cards come in and the light walks
   * their edges.
   */
  arriving?: { ids: string[]; stage: 'ceremony' | 'landed' } | null;
  /**
   * "Was this any help?" (2026-09-15): the one question over the composer
   * after an Ember has landed, while the page has it open. `paused` holds its
   * clock (an Ember page is open over the thread); `onClose` hears how it
   * ended the moment it does; `onDone` fires after the fade, to unmount it.
   */
  helpAsk?: {
    paused: boolean;
    onClose: (outcome: HelpAskOutcome, answer?: HelpAskAnswer) => void;
    onDone: () => void;
  } | null;
  /**
   * The person beside their own turns (2026-09-13): their picture, and what a
   * tap on it does — the page opens the avatar sheet. '' while the picture is
   * not known yet draws the picker's default combo.
   */
  viewer: { avatarKey: string; onChangeAvatar: () => void };
  /**
   * A signed-out person's door to the same conversation on their phone
   * (2026-09-16, §4.5; 2026-10-01: one place): `composerLine` is one line
   * over the field, for as long as the page passes it. The page decides; the
   * shell only places it.
   */
  composerLine?: ReactNode;
  /**
   * A guest's invite to join (2026-10-27 founder; GuestDoor.tsx): the card the page passes, drawn after the reply it
   * first came with (`after`). The page decides when; the shell only places it.
   */
  guestInvite?: { after: number; node: ReactNode } | null;
  /** A guest's door (GuestDoor.tsx): drawn where they type, in the field's place, for as long as the page passes it. */
  composerDoor?: ReactNode;
  /** Each keystroke in the field (a room: the seat's typing goes to Stream; 2026-09-30). Throttling is the caller's. */
  onKeystroke?: () => void;
  /** The mic was pressed to record (the silent start's clock stops: something was begun). */
  onRecordStart?: () => void;
  /** The field's placeholder; the conversation's own unless the page names one (a room: "Say something to the room"). */
  composerPlaceholder?: string;
  /**
   * When the crisis resources were last placed in this conversation
   * (2026-09-17, edge system-notices.js): the card — the crisis lines of the
   * person's country (`crisisLines`), in the product's voice — is drawn
   * after the last turn written before that moment. Null: never.
   */
  crisisReferredAt?: string | null;
  /**
   * The age check standing on this person (edge age-check.js, 2026-10-27): its question after the turns, until it is
   * answered. `onAnswered` hears the answer once the edge has it.
   */
  ageCheck?: { onAnswered: (answer: 'adult' | 'under_18', reply: AgeCheckAnswer) => void } | null;
  /**
   * When the lines for a risk of hurting someone else were last placed
   * (2026-10-12, edge system-notices.js): their card is drawn the same way,
   * after the last turn written before that moment. Null: never.
   */
  harmReferredAt?: string | null;
  /** The two cards' words, in the person's country's lines (2026-10-29, edge system-notices.js CRISIS_LINES). */
  crisisLines?: CrisisLines | null;
  /**
   * In a room's thread (§4.21): what a person can do with one line — delete
   * their own, report another's — rendered under that bubble. Absent in a
   * conversation.
   */
  lineActions?: (m: ConversationMessage) => ReactNode;
  /**
   * In a room's thread (小屋留人需求 三/四, 2026-10-19): the emoji under a line
   * (`lineReactions`, the capsules at the bubble's lower edge), the faces of the
   * others who have read up to it (`lineReaders`, small at the bubble's lower
   * right corner), and what a long press — or a right click — on the bubble does
   * (`onLineHold`, the quick bar of emoji over it). Absent in a conversation.
   */
  lineReactions?: (m: ConversationMessage) => ReactNode;
  lineReaders?: (m: ConversationMessage) => ReactNode;
  onLineHold?: (m: ConversationMessage, bubble: HTMLElement) => void;
  /**
   * In a room's thread, when Ember left a note for a person coming back
   * (2026-10-09 拍板): the line they had read through. A quiet divider stands
   * before the first line from the others after it. Absent: none.
   */
  newAfter?: number | null;
}

/**
 * What the product says in the thread in its own voice, not Ember's
 * (2026-09-17; edge system-notices.js, the same words on every door — since
 * 2026-10-29 the lines of the person's country, sent with the conversation):
 * the crisis card, following the turn that stood when the edge placed it. What
 * Ember is stands under the composer as one quiet line, always there
 * (`.convo-ai-line`, the way ChatGPT's "can make mistakes" line stands) — the
 * 2026-09-17 二次拍板 replaced a head-of-thread notice with it.
 */
function CrisisCard({ text }: { text: string }): ReactElement {
  return (
    <div className="convo-crisis" role="note" aria-label="Crisis resources">
      <p>{text}</p>
    </div>
  );
}

/** The turn the crisis card follows: the last turn written before the placement; -1 = the head of the thread. */
function crisisAnchorTurn(messages: ConversationMessage[], referredAt: string | null | undefined): number | null {
  if (!referredAt) return null;
  const at = new Date(referredAt).getTime();
  if (!Number.isFinite(at)) return null;
  let anchor = -1;
  for (const m of messages) {
    if (!m.created_at) continue;
    const t = new Date(m.created_at).getTime();
    if (Number.isFinite(t) && t <= at) anchor = m.turn_index;
  }
  return anchor;
}

/**
 * S05 — the one conversation UI every entry lands in (§4.5). No Receive/Leave
 * role choice; just telling. Two people, each beside their own words
 * (2026-09-13): Ember's turns sit left with Ember's face for that turn on a
 * paper disc, the person's turns right with their picture; every bubble has
 * a tail toward whoever said it. The person's picture opens the avatar
 * sheet; Ember's face is not a control. Stop aborts the in-flight reply. Nobody ends the conversation
 * (2026-09-12): it has no exit — the person talks, and when they have been
 * quiet a while Ember writes what was told into an Ember, which appears here
 * in the thread as a paper card (`embers`). The conversation hangs from the
 * composer (messages bottom-anchored, the way every messaging app reads)
 * instead of from the top of the screen.
 *
 * The + button hands Ember a real photo mid-telling. It is a plain file input
 * behind a styled label, which is deliberate: a file picker is a user gesture
 * the browser answers with exactly the chosen file, so it asks for no
 * permission on any platform. (`capture` would force the camera and pull the
 * OS camera-permission prompt in — it is not set, and the native sheet still
 * offers "Take Photo" for anyone who wants it.)
 */
const photoCount = (m: ConversationMessage): number => m.attachments?.length ?? 0;

/**
 * A notice turn as a line (§4.14; 2026-10-19 拍板, replacing the dashed bubble
 * and its worded way in): an icon where Ember's face would be, then what
 * happened in bold and the specifics after it (`notice.head` / `notice.body`,
 * the text's own first two paragraphs). The icon names the family the fact is
 * of, not the fact (2026-10-21 founder): a room (the house), the person's
 * Embers being read and the notes on them (the book with a heart), the terms
 * (the page) — in the quiet notice colour. A notice whose kind is not known
 * wears the bell.
 */
function noticeLine(m: ConversationMessage): ReactNode {
  const kind = m.notice?.kind ?? '';
  const Icon = kind.startsWith('room_') ? House
    : kind === 'read_note' || kind === 'read_reply' || kind === 'reads_weekly' ? BookHeart
    : kind === 'legal_update' ? FileText
    : Bell;
  return (
    <>
      <span className="convo-notice-icon" aria-hidden><Icon size={17} strokeWidth={2} /></span>
      <span className="convo-notice-text" dir="auto">
        <b>{m.notice?.head}</b>{m.notice?.body ? ` ${m.notice.body}` : ''}
        {m.notice?.destination ? <span className="convo-notice-go" aria-hidden> ›</span> : null}
      </span>
    </>
  );
}

/**
 * A long press on a bubble (touch: held 450 ms without moving) or a right click
 * (2026-10-19, rooms): `onHold` gets the bubble to anchor to. A press that moves
 * is a scroll and is let go; the browser's own menu and text callout give way
 * only on a room's bubbles, where this is mounted.
 */
const HOLD_MS = 450;
function holdHandlers(m: ConversationMessage, onHold: (m: ConversationMessage, el: HTMLElement) => void) {
  let timer: number | null = null;
  let start: { x: number; y: number } | null = null;
  const clear = (): void => { if (timer !== null) window.clearTimeout(timer); timer = null; start = null; };
  return {
    onContextMenu: (e: ReactMouseEvent<HTMLElement>) => { e.preventDefault(); clear(); onHold(m, e.currentTarget); },
    onPointerDown: (e: PointerEvent<HTMLElement>) => {
      if (e.pointerType === 'mouse') return;
      const el = e.currentTarget;
      start = { x: e.clientX, y: e.clientY };
      timer = window.setTimeout(() => { timer = null; onHold(m, el); }, HOLD_MS);
    },
    onPointerMove: (e: PointerEvent<HTMLElement>) => {
      if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > 8) clear();
    },
    onPointerUp: clear,
    onPointerCancel: clear,
    style: { WebkitTouchCallout: 'none' } as CSSProperties,
  };
}

export function ConversationShell({
  messages, typing, disabled, onSend, onStop, retry = null, starters, photos, voice, logConversationId, logRoomId, releaseAfterSend = false,
  embers = [], emberNote, onOpenEmber, arriving = null, helpAsk = null, viewer, composerLine, guestInvite = null, composerDoor, composerPlaceholder, onKeystroke, onRecordStart,
  crisisReferredAt = null, harmReferredAt = null, crisisLines = null, ageCheck = null, draft = null, draftKey, lineActions, lineReactions, lineReaders, onLineHold, history = null, shareAsk = null, reads = [], precedents = [], joinCards = [], featureCards = [], newAfter = null,
}: ConversationShellProps): ReactElement {
  // Every control in the shell names its thread: the conversation, and in a
  // room the room too (2026-10-04: a room's talk_mic_* / talk_stop / composer
  // rows were indistinguishable from Ember's own conversation's).
  const convoLogAttrs = {
    ...(logConversationId ? { 'data-log-conversation-id': logConversationId } : {}),
    ...(logRoomId ? { 'data-log-match-id': logRoomId } : {}),
  };
  // Whether the thread's head is here: nothing older exists, or was not asked
  // for. What belongs before the first held turn (a legacy Ember, a crisis
  // card placed before the window) is drawn at the head only then — with
  // older turns still to come it waits for them.
  const headHere = !history?.hasMore;
  const crisisAfter = crisisAnchorTurn(messages, crisisReferredAt);
  const harmAfter = crisisAnchorTurn(messages, harmReferredAt);
  // Where each Ember card sits: after the last turn that existed when it was
  // written. One written before any turn this thread still holds (a legacy
  // row) leads the thread.
  const firstTurn = messages.length > 0 ? messages[0].turn_index : -1;
  // The first line from the others after where a person coming back had read (a room's note, 2026-10-09).
  const firstNew = newAfter === null ? null : (messages.find((m) => m.turn_index > newAfter && (m.role === 'peer' || m.role === 'assistant') && !m.notice?.folded)?.turn_index ?? null);
  const embersAfter = (turnIndex: number): ConversationEmber[] => embers.filter((e) => (
    e.after_turn_index === turnIndex || (headHere && turnIndex === firstTurn && e.after_turn_index < firstTurn)
  ));
  const readsAfter = (turnIndex: number): ConversationReadItem[] => reads.filter((r) => (
    r.after_turn_index === turnIndex || (headHere && turnIndex === firstTurn && r.after_turn_index < firstTurn)
  ));
  const precedentsAfter = (turnIndex: number): ConversationPrecedentItem[] => precedents.filter((p) => (
    p.after_turn_index === turnIndex || (headHere && turnIndex === firstTurn && p.after_turn_index < firstTurn)
  ));
  const joinCardsAfter = (turnIndex: number): ConversationJoinCard[] => joinCards.filter((j) => (
    j.after_turn_index === turnIndex || (headHere && turnIndex === firstTurn && j.after_turn_index < firstTurn)
  ));
  // A group card that no longer stands (`gone`, Groups 2026-11-03) is not drawn, and ends no run of lines.
  const featureCardsAfter = (turnIndex: number): ConversationFeatureCard[] => featureCards.filter((c) => !c.gone && (
    c.after_turn_index === turnIndex || (headHere && turnIndex === firstTurn && c.after_turn_index < firstTurn)
  ));
  const [input, setInput] = useState('');
  // What the next turn is about (talk-about.ts): set with the draft, dropped
  // by the chip's ×, cleared once a turn has carried it.
  const [about, setAbout] = useState<TurnAbout | null>(null);
  useEffect(() => {
    if (!draft) return;
    setInput(draft.text);
    setAbout(draft.about);
    inputRef.current?.focus();
  }, [draft]);

  /**
   * A character that landed at the head of the field while text stood in it —
   * see registry `conversation.composer_head_insert`. The redraw clock is the
   * thread's, not the field's: it moves when `messages` changes (a poll, a new
   * turn), never on a keystroke, so the row can say whether a redraw came
   * just before the caret was found at the start.
   */
  const lastRedrawRef = useRef(0);
  useEffect(() => { lastRedrawRef.current = performance.now(); }, [messages]);
  const lastInputAtRef = useRef(0);
  const onInputChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    const next = e.target.value;
    const now = performance.now();
    const added = next.length - input.length;
    const caret = e.target.selectionStart ?? next.length;
    if (input.length > 0 && added > 0 && caret === added && next.endsWith(input)) {
      track('conversation.composer_head_insert', {
        ...(logConversationId ? { conversation_id: logConversationId } : {}),
        ...(logRoomId ? { match_id: logRoomId } : {}),
        t_ms: Math.round(now),
        chars: next.length,
        gap_ms: lastInputAtRef.current ? Math.round(now - lastInputAtRef.current) : null,
        since_redraw_ms: Math.round(now - lastRedrawRef.current),
        keyboard: keyboardKind(),
      });
    }
    lastInputAtRef.current = now;
    if (added > 0) onKeystroke?.();
    setInput(next);
  };

  /**
   * The first character of this telling — see registry `conversation.drafted`.
   * Watched on the value rather than bound to onChange so that dictation and
   * paste count as beginning to write, which they are; a send that fails and
   * puts the text back is not a second beginning, which is what the ref is
   * for. A starter tap never lands here: it sends without passing through the
   * composer.
   */
  const draftedRef = useRef('');
  useEffect(() => {
    const convId = logConversationId || '';
    if (!input.trim() || draftedRef.current === (convId || 'unkeyed')) return;
    draftedRef.current = convId || 'unkeyed';
    track('conversation.drafted', {
      ...(convId ? { conversation_id: convId } : {}),
      t_ms: Math.round(performance.now()),
      keyboard: keyboardKind(),
    });
  }, [input, logConversationId]);

  /**
   * How the attempt ended — see registry `conversation.composer_engaged`.
   * Kept in refs, not state: none of it is drawn, and a re-render per keystroke
   * to count keystrokes would be its own bug.
   */
  const firstFocusRef = useRef<number | null>(null);
  const maxCharsRef = useRef(0);
  const sendsRef = useRef(0);
  const draftRef = useRef('');
  const lastReportRef = useRef('');
  useEffect(() => {
    draftRef.current = input;
    if (input.length > maxCharsRef.current) maxCharsRef.current = input.length;
  }, [input]);

  const reportComposer = useCallback((): void => {
    const convId = logConversationId || '';
    // The local UI preview is not a telling and must not mint rows.
    if (!convId || convId.startsWith('local-')) return;
    // An untouched composer is REPORTED, not skipped: "never focused" is the
    // first line of the table this event exists to fill, and it is the largest
    // group — the person who read the opener and left without reaching for the
    // field. Skipping it (this function's first version, 2026-09-07) cost the
    // denominator: of 34 silent tellings that evening exactly ONE left a row,
    // so every rate computed from these rows was a rate among people who had
    // already decided to write. page.engaged does not cover it — that row is
    // the page's, carries no conversation_id, and cannot be tied to a telling.
    const snapshot = [
      convId, firstFocusRef.current, maxCharsRef.current, draftRef.current.length, sendsRef.current,
    ].join('|');
    if (snapshot === lastReportRef.current) return;
    lastReportRef.current = snapshot;
    track('conversation.composer_engaged', {
      conversation_id: convId,
      focused: firstFocusRef.current !== null,
      ...(firstFocusRef.current !== null ? { first_focus_ms: firstFocusRef.current } : {}),
      max_chars: maxCharsRef.current,
      draft_chars: draftRef.current.length,
      sends: sendsRef.current,
      keyboard: keyboardKind(),
    });
  }, [logConversationId]);

  // Same teardown contract as page.engaged: `hidden` is the last moment a
  // beacon is guaranteed (iOS often never fires pagehide), and an in-app
  // webview swapping back to its host app is precisely the visit that would
  // otherwise leave no trace. Unmount covers leaving the page inside the SPA.
  useEffect(() => {
    const onHide = (): void => { if (document.visibilityState === 'hidden') reportComposer(); };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', reportComposer);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', reportComposer);
      reportComposer();
    };
  }, [reportComposer]);

  // The New door swaps the telling under a composer that never unmounts, so
  // the counters have to start again with it — otherwise the second telling
  // inherits the first one's writing and the row says a person wrote before
  // they had seen the page. Declared after the reporter so React runs that
  // effect's cleanup — the previous telling's final row — before this resets.
  // The draft itself is NOT cleared here: whatever is in the field belongs to
  // whoever is looking at it now, and it is already this telling's draft.
  useEffect(() => {
    firstFocusRef.current = null;
    maxCharsRef.current = input.length;
    sendsRef.current = 0;
    draftRef.current = input;
    lastReportRef.current = '';
  }, [logConversationId]); // eslint-disable-line react-hooks/exhaustive-deps
  const keepFieldFocus = (e: PointerEvent<HTMLButtonElement>): void => { e.preventDefault(); };
  // Stop takes the send slot the moment a reply is asked for, so a second tap
  // on Send — or a tap on the voice ↑ still transcribing — lands on it (prod,
  // 45 days to 2026-10-17: 24 of 60 Stops came within 0.7 s of the turn
  // landing, when no reply can be on screen yet, and the person met a turn
  // Ember never answered). For its first second in the slot Stop holds:
  // the tap is logged (state held) and does nothing.
  const [stopArmed, setStopArmed] = useState(false);
  useBrowserLayoutEffect(() => {
    setStopArmed(false);
    if (!typing) return undefined;
    const t = window.setTimeout(() => setStopArmed(true), STOP_ARM_MS);
    return () => window.clearTimeout(t);
  }, [typing]);
  // The viewer receives the whole set the tapped image belongs to (one
  // message's photos, or the tray), so a reader can swipe between them.
  const [preview, setPreview] = useState<{ images: string[]; index: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Voice input (ChatGPT-app shape): the mic swaps the field for a waveform
  // with cancel on the left and done on the right; done sends the clip off
  // and, while the words are on their way, the same row shows a pulse. The
  // words land in the field appended to whatever was already typed.
  const recorder = useVoiceRecorder();
  const [transcribing, setTranscribing] = useState(false);
  // A newer deploy reloads the page (stale-bundle-reload): the words in the
  // field are kept as they are typed and come back; a clip being recorded is
  // told first and given a few seconds; one on its way to words is let arrive.
  useStaleReloadHold(recorder.recording, 'warn');
  useStaleReloadHold(transcribing);
  useKeptAcrossReload(
    draftKey ? `${DRAFT_STORE_PREFIX}${draftKey}` : null,
    input.trim() ? JSON.stringify({ text: input, about }) : '',
    (kept) => {
      // A door's sentence (`draft`: "Have thoughts about this?" on a letter or
      // an Ember) is the person's choice of this moment and wins over what the
      // field kept from an earlier visit — otherwise the chip over the field
      // names the letter they came from last time, not the Ember they just
      // tapped (dev 2026-10-01). The kept copy is overwritten by the mirror on
      // the next render.
      if (draft) return;
      const saved = JSON.parse(kept) as { text?: unknown; about?: TurnAbout | null };
      if (typeof saved.text !== 'string' || !saved.text.trim()) return;
      setInput(saved.text);
      setAbout(saved.about ?? null);
    },
  );
  const startRecording = async (): Promise<void> => {
    onRecordStart?.();
    try {
      await recorder.start();
    } catch (err) {
      voice.onMicUnavailable(err instanceof Error && err.message === 'denied' ? 'denied' : 'unsupported');
    }
  };
  // ⏹ puts the words in the field to read first; ↑ is ⏹ plus Send in one
  // tap. Either way the words join whatever was already typed.
  const finishRecording = async (then: 'field' | 'send'): Promise<void> => {
    let clip: Blob;
    try {
      clip = await recorder.finish();
    } catch {
      // The tap did nothing visible; the record is the only trace.
      trackFailed('chat_transcribe', 'recording_failed');
      return;
    }
    setTranscribing(true);
    const text = await voice.transcribe(clip, then, input);
    setTranscribing(false);
    if (!text) return;
    const full = input.trim() ? `${input.trimEnd()} ${text}` : text;
    if (then === 'send' && !typing && !uploading && !disabled) {
      setInput('');
      inputRef.current?.blur();
      const ok = await onSend(full, readyIds);
      if (ok) sendsRef.current += 1; else setInput(full);
      return;
    }
    setInput(full);
    // Focus after React has re-rendered the field back into the row.
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const pending = photos.pending;
  const uploading = pending.some((p) => p.uploading);
  const readyIds = pending.filter((p) => !p.uploading).map((p) => p.mediaId);
  const canSend = !disabled && !uploading && (input.trim().length > 0 || readyIds.length > 0);

  // Pinned to the newest turn. The first time is a jump before the first
  // paint, not a scroll: the thread opens at its end, the way a chat does —
  // the scroller's smooth scrolling is for a new turn arriving, and on a long
  // history it made the whole thread fly past on every page load, after a
  // frame of its beginning (2026-09-15).
  const pinnedRef = useRef(false);
  const lastTurn = messages.length > 0 ? messages[messages.length - 1] : null;
  useBrowserLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    // After the first time, a turn arriving pulls the thread to its end only for a reader already at the end, or for
    // their own words (2026-10-27 walk-through: in a busy room, scrolled up to read or react, every new line flew the
    // reader to the bottom).
    if (pinnedRef.current && !nearBottomRef.current && lastTurn?.role !== 'user') return;
    scroller.scrollTo({ top: scroller.scrollHeight, behavior: pinnedRef.current ? 'smooth' : 'instant' });
    pinnedRef.current = true;
    // Keyed on the newest turn — its index and, as a reply grows while it is
    // written, its length — not on how many turns are held: older turns
    // arriving at the head (history) must not fly the thread to its end.
  }, [lastTurn?.turn_index, lastTurn?.content.length, typing, pending.length, retry]);

  // Older turns prepended (history): the turn that was at the head stays
  // where the reader sees it. Its place is taken when the reach back is
  // asked for — where that turn sat against the top of the viewport — and
  // restored after the paint that put turns above it. Anchored on the turn,
  // not on the scroll height, so a turn arriving at the foot meanwhile
  // changes nothing.
  const anchorRef = useRef<{ turn: number; offset: number } | null>(null);
  const firstIndex = messages.length > 0 ? messages[0].turn_index : null;
  const turnEl = (turn: number): HTMLElement | null => scrollRef.current?.querySelector<HTMLElement>(`[data-turn="${turn}"]`) ?? null;
  useBrowserLayoutEffect(() => {
    const scroller = scrollRef.current;
    const anchor = anchorRef.current;
    if (!scroller || !anchor || firstIndex === null || firstIndex >= anchor.turn) return;
    anchorRef.current = null;
    const el = turnEl(anchor.turn);
    if (!el) return;
    const prevBehavior = scroller.style.scrollBehavior;
    scroller.style.scrollBehavior = 'auto';
    scroller.scrollTop = el.offsetTop - anchor.offset;
    scroller.style.scrollBehavior = prevBehavior;
  }, [firstIndex]);

  // A window shorter than the scroller cannot be scrolled to its head, so
  // nothing would ever ask for the turns before it: while there are more and
  // what is held does not fill the box, the next window is asked for as
  // soon as the last one has been drawn.
  const historyRef = useRef(history);
  historyRef.current = history;
  useEffect(() => {
    const scroller = scrollRef.current;
    const h = historyRef.current;
    if (!scroller || !h?.hasMore || h.loading) return;
    if (scroller.scrollHeight <= scroller.clientHeight + 240) h.onLoadOlder();
  }, [firstIndex, history?.hasMore, history?.loading]);

  // Whether the person is reading the latest turns rather than scrolled back
  // into history. Sized against the keyboard-shrunk viewport, so it means
  // "would expect to still see the newest message".
  const nearBottomRef = useRef(true);
  const onScrolled = (): void => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    nearBottomRef.current = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 120;
    // The head of what is held is in view: ask for the turns before it. The
    // page holds one reach back at a time; `loading` keeps this from asking
    // again on every scroll event while it is on the wire.
    if (history?.hasMore && !history.loading && scroller.scrollTop < 240 && firstIndex !== null) {
      const el = turnEl(firstIndex);
      anchorRef.current = el ? { turn: firstIndex, offset: el.offsetTop - scroller.scrollTop } : null;
      history.onLoadOlder();
    }
  };

  // The scroller shrinks under the reader — software keyboard opening, the
  // composer growing a line — and the browser keeps scrollTop, which cuts off
  // the newest turns at exactly the moment they matter. Re-pin to the bottom
  // whenever the box resizes, unless the person has scrolled back on purpose.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (!nearBottomRef.current) return;
      // Instant, not smooth: this fires every frame of the keyboard
      // animation, and a smooth scroll would trail it visibly.
      const prev = scroller.style.scrollBehavior;
      scroller.style.scrollBehavior = 'auto';
      scroller.scrollTop = scroller.scrollHeight;
      scroller.style.scrollBehavior = prev;
    });
    ro.observe(scroller);
    return () => ro.disconnect();
  }, []);

  // The thread also grows at its foot after the newest turn is drawn — a
  // shared Ember the person said yes to lands with the page's next fetch of
  // the conversation, seconds after the reply that gave it (prod 2026-10-17,
  // 9c173682: the card stood below the screen and was never opened), a
  // page of how it went, a join card, a cover filling in. The same rule:
  // a person reading the newest turns sees what came in under them. Smooth,
  // as a new turn arrives.
  useEffect(() => {
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (!scroller || !content || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (nearBottomRef.current) scroller.scrollTop = scroller.scrollHeight;
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, []);

  // Grow with the telling instead of making the person type through a
  // one-line slot. Eight lines is the ceiling; after that the field itself
  // scrolls so the composer cannot take over the conversation viewport.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxHeight = Number.parseFloat(window.getComputedStyle(el).maxHeight);
    const nextHeight = Number.isFinite(maxHeight) ? Math.min(el.scrollHeight, maxHeight) : el.scrollHeight;
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = Number.isFinite(maxHeight) && el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [input]);

  const send = async (): Promise<void> => {
    const text = input.trim();
    // A photo on its own is a message. Only a turn with neither is not.
    if (!canSend || typing) return;
    // Let go of focus BEFORE clearing (2026-10-03, prod a9ff34e7): with the
    // field focused, Android's keyboard keeps its composition — Gboard's
    // voice typing composes a whole phrase — and writes it back into the
    // emptied field, so the next message began with the tail of the one just
    // sent. Blurring commits the composition first; the dictation path below
    // (finishRecording 'send') already did this. Focus returns in the timeout.
    inputRef.current?.blur();
    setInput('');
    // Sending ends the turn, so the field lets go of focus right here (拍板
    // 2026-09-08 拍板(推翻 09-03 的收键盘):发送后焦点留在输入框——多数人
    // 发完还有下一句,收了键盘就得再点一下才能接着说。Finish 槽位不受损:它
    // 本来就只在「没在打字」时出现,人自己收起键盘它照旧露出来。失败时字放
    // 回去,焦点同样留着,toast 说明白了,人接着改。
    const ok = await onSend(text, readyIds, about);
    if (ok) { sendsRef.current += 1; setAbout(null); } else setInput(text);
    // 点按可能把焦点让给了按钮;等 React 把行重画完再拿回来。和人聊的地方在手机上不拿回(releaseAfterSend,2026-10-27
    // founder:发完键盘收起、看对话);桌面没有要收的键盘,照旧拿回。
    if (releaseAfterSend && window.matchMedia?.('(pointer: coarse)').matches) return;
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  const onFilesPicked = (e: ChangeEvent<HTMLInputElement>): void => {
    const picked = Array.from(e.target.files ?? []);
    // Reset first: picking the same file twice in a row must fire again.
    e.target.value = '';
    if (picked.length === 0) return;
    if (photos.onPickPhotos) { photos.onPickPhotos(picked); return; }
    for (const file of picked.slice(0, Math.max(0, photos.remaining))) {
      void photos.onPickPhoto(file);
    }
  };

  let lastAssistantIndex = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].role === 'assistant') { lastAssistantIndex = i; break; }
  }
  // The reply being written (2026-09-21): Ember's turn is already the newest
  // thing in the thread while the reply is still in flight. Its face is
  // alive, and the typing dots make way for the words.
  const writing = typing && lastAssistantIndex === messages.length - 1;
  // Where the share ask stands: the turn Ember voiced it in when that turn is
  // held here, else Ember's last turn.
  const voicedHere = shareAsk?.voiced_turn !== null && shareAsk?.voiced_turn !== undefined
    && messages.some((m) => m.role === 'assistant' && m.turn_index === shareAsk.voiced_turn);
  const shareAskAfter = shareAsk ? (voicedHere ? shareAsk.voiced_turn : (lastAssistantIndex >= 0 ? messages[lastAssistantIndex].turn_index : null)) : null;
  // Lines from one sender in a row are one group (2026-10-20, the chat's skins): the inner corners tighten, the
  // picture and a room member's name stand by the first line only. Anything drawn between two turns — a card, a
  // retry line, the "new since" rule — ends the group.
  const senderOf = (m: ConversationMessage | undefined): string => (
    !m || m.role === 'system' || m.source === 'notice' || m.source === 'letter' ? '' : m.role === 'peer' ? `peer:${m.speaker?.name ?? ''}` : m.role
  );
  const standsAfter = (turn: number): boolean => crisisAfter === turn || harmAfter === turn || shareAskAfter === turn
    || (retry?.afterTurn === turn && !typing) || embersAfter(turn).length > 0 || readsAfter(turn).length > 0
    || precedentsAfter(turn).length > 0 || joinCardsAfter(turn).length > 0 || featureCardsAfter(turn).length > 0;
  const joinsPrevious = (i: number): boolean => i > 0 && senderOf(messages[i]) !== '' && senderOf(messages[i]) === senderOf(messages[i - 1])
    && !standsAfter(messages[i - 1].turn_index) && messages[i].turn_index !== firstNew;
  const groupClass = (i: number): string => `${joinsPrevious(i) ? ' is-cont' : ''}${joinsPrevious(i + 1) ? ' has-next' : ''}`;

  return (
    <div className="convo">
      {recorder.recording && (
        <div className="convo-mic-live" role="status">
          <span className="convo-mic-live-dot" aria-hidden />
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="9" y="3" width="6" height="11" rx="3" />
            <path d="M6 11a6 6 0 0 0 12 0M12 17v4" />
          </svg>
          <span>Listening</span>
        </div>
      )}
      <div ref={scrollRef} className="convo-scroll" role="log" aria-live="polite" aria-label="Conversation" onScroll={onScrolled}>
        <div ref={contentRef} className="convo-messages">
          {history?.hasMore && (
            <p className="convo-history" aria-live="polite">{history.loading ? 'Loading earlier…' : 'Scroll up for earlier'}</p>
          )}
          {headHere && crisisLines && crisisAfter === -1 && <CrisisCard text={crisisLines.self_harm} />}
          {headHere && crisisLines && harmAfter === -1 && <CrisisCard text={crisisLines.harm} />}
          {messages.map((m, i) => (<Fragment key={m.turn_index}>
            {m.turn_index === firstNew && <p className="convo-new-since" data-new-since>New since you were here</p>}
            {m.role === 'system' ? <p className="convo-system" dir="auto" data-turn={m.turn_index}>{m.content}</p> : m.source === 'notice' ? (
              // A notice (§4.14): a fact the runtime sent, not something Ember said — a line in her column; the
              // whole line is the way in when its fact lives on a page (2026-09-30), as the link is in the text. A line
              // `folded` into the newer one about the same thing right after it is not drawn (2026-10-21).
              m.notice?.folded ? null : m.notice?.destination
                ? <Link className="convo-notice" to={m.notice.destination} data-turn={m.turn_index} data-log="talk_notice_open" data-log-state={m.notice.kind} {...convoLogAttrs}>{noticeLine(m)}</Link>
                : <div className="convo-notice" data-turn={m.turn_index}>{noticeLine(m)}</div>
            ) : (
            <div className={`convo-turn is-${m.role}${groupClass(i)}`} data-turn={m.turn_index}>
              {m.role === 'peer'
                ? <span className="convo-avatar is-peer" aria-hidden><PresenceAvatar avatarKey={m.speaker?.avatar || ''} size={32} /></span>
                : m.role === 'assistant'
                ? (
                  // Only Ember's newest turn is alive (breathing, a faint halo
                  // round the disc); every earlier face is a still frame.
                  <span className={`convo-avatar-wrap${(!typing || writing) && i === lastAssistantIndex ? ' is-live' : ''}`}>
                    <span className="convo-avatar is-ember"><EmberMascot face={m.face ?? 'listening'} still={(typing && !writing) || i !== lastAssistantIndex} interactive={false} className="convo-avatar-face" /></span>
                  </span>
                )
                : (
                  <button
                    type="button"
                    className="convo-avatar is-you"
                    aria-label="Change your picture"
                    data-log="talk_avatar_open"
                    {...convoLogAttrs}
                    onClick={viewer.onChangeAvatar}
                  >
                    <PresenceAvatar avatarKey={viewer.avatarKey} size={32} />
                  </button>
                )}
              <div className="convo-turn-body">
                {/* The name in its seat's colour (the skin's three, by seat). */}
                {m.role === 'peer' && !joinsPrevious(i) && <span className={`convo-peer-name is-seat-${((m.speaker?.seat ?? 0) % 3) + 1}`} dir="auto">{m.speaker?.name || 'Someone'}</span>}
                {/* Photos ride OUTSIDE the bubble, always: a picture inside the
                    dark user skin reads as a heavy frame around it. Caption and
                    photos stack as two pieces of one turn. */}
                {photoCount(m) > 0 && (
                  <span className={`convo-photos count-${photoCount(m) >= 7 ? 'grid' : photoCount(m)}`}>
                    {m.attachments?.map((a: MessageAttachment, i: number) => (
                      <button
                        key={a.media_id}
                        type="button"
                        className="convo-photo-preview"
                        data-log="photo_preview_open"
                        onClick={() => setPreview({
                          images: (m.attachments ?? []).map((x) => x.url),
                          index: i,
                        })}
                        aria-label="Preview image"
                      >
                        <img src={a.url} alt="Shared photo" loading="lazy" />
                      </button>
                    ))}
                  </span>
                )}
                {m.content && m.role === 'assistant' && m.source === 'letter' ? (
                  // Ember's weekly letter (§4.20, 2026-09-23 二次拍板): the
                  // turn is the letter's cover — the same object as on the
                  // notebook's Letters tab — and opening it opens the letter's
                  // page, the way an Ember card opens the Ember. The turn's
                  // content is the letter as prose (what the talk model reads
                  // back); the cover shows its first line.
                  <LetterCover
                    className="convo-letter-cover"
                    to={m.letter_id ? `/me/letters/${encodeURIComponent(m.letter_id)}` : '/me/letters'}
                    writtenAt={m.created_at}
                    firstLine={firstSentence(m.content)}
                    log="talk_letter_open"
                    letterId={m.letter_id ?? null}
                  />
                ) : m.content && (
                  <div className="convo-bubble-wrap" {...(onLineHold && (m.role === 'user' || m.role === 'peer' || m.role === 'assistant') ? holdHandlers(m, onLineHold) : {})}>
                    <div className="convo-bubble" dir="auto">
                      <span className="convo-bubble-text">{m.content}</span>
                    </div>
                    {lineReaders && lineReaders(m)}
                  </div>
                )}
                {lineReactions && lineReactions(m)}
                {retry && m.role === 'user' && retry.afterTurn === m.turn_index && !typing && (
                  <div className="convo-retry" role="status">
                    {retry.unanswered ? (
                      // Opened on the person's words with nothing coming for them (2026-10-17, jam 拍板): the way to an
                      // answer alone — the page cannot say why nothing answered (a Stop, a failure, a text unanswered).
                      <button type="button" className="convo-retry-btn" data-log="talk_unanswered_reply" {...convoLogAttrs} onClick={retry.onRetry}>Let Ember reply</button>
                    ) : retry.stopped ? (
                      // The person stopped this reply (2026-10-17 jam, A): said once, with the way back to an answer.
                      <>
                        <span>Stopped.</span>
                        <button type="button" className="convo-retry-btn" data-log="talk_stop_reply" {...convoLogAttrs} onClick={retry.onRetry}>Let Ember reply</button>
                      </>
                    ) : (
                      <>
                        <span>Ember lost the thread for a moment.</span>
                        <button type="button" className="convo-retry-btn" data-log="talk_reply_retry" {...convoLogAttrs} onClick={retry.onRetry}>Try again</button>
                      </>
                    )}
                  </div>
                )}
                {lineActions && (m.role === 'user' || m.role === 'peer' || m.role === 'assistant') && <div className="convo-line-acts">{lineActions(m)}</div>}
              </div>
            </div>
            )}
            {crisisLines && crisisAfter === m.turn_index && <CrisisCard text={crisisLines.self_harm} />}
            {crisisLines && harmAfter === m.turn_index && <CrisisCard text={crisisLines.harm} />}
            {embersAfter(m.turn_index).map((e) => (
              <EmberCard
                key={e.id}
                ember={e}
                note={emberNote}
                logAttrs={convoLogAttrs}
                arriving={arriving?.ids.includes(e.id) ? arriving.stage : null}
                onOpen={onOpenEmber}
              />
            ))}
            {readsAfter(m.turn_index).map((r) => <ReadCard key={r.id} read={r} logAttrs={convoLogAttrs} />)}
            {precedentsAfter(m.turn_index).map((p) => <PrecedentCard key={p.id} page={p} logAttrs={convoLogAttrs} />)}
            {joinCardsAfter(m.turn_index).map((j) => <JoinCard key={`${j.what}:${j.state}:${j.after_turn_index}`} card={j} />)}
            {featureCardsAfter(m.turn_index).map((c) => <FeatureCard key={`${c.what}:${c.room_id ?? c.post_id ?? c.group_id ?? ''}:${c.after_turn_index}`} card={c} logAttrs={convoLogAttrs} />)}
            {shareAsk && shareAskAfter === m.turn_index && (
              <div className="convo-share-ask">
                <ShareAskCard ask={shareAsk} answer={null} onAnswered={shareAsk.onAnswered} onReply={shareAsk.onReply} />
              </div>
            )}
            {guestInvite && guestInvite.after === m.turn_index && guestInvite.node}
          </Fragment>))}
          {ageCheck && <AgeCheckQuestion onAnswered={ageCheck.onAnswered} />}
          {typing && !writing && (
            <div className="convo-turn is-assistant">
              <span className="convo-avatar-wrap is-live"><span className="convo-avatar is-ember"><EmberMascot face="thinking" interactive={false} className="convo-avatar-face" /></span></span>
              <div className="convo-turn-body">
                <div className="convo-bubble-wrap">
                  <div className="convo-bubble convo-typing" aria-label="Ember is writing">
                    <span /><span /><span />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* data-eh-keyboard-zone: keyboard-viewport.ts rule 5 — while the
          keyboard height is a guess (a webview that reports nothing), a drag
          that starts anywhere outside this zone blurs the field. */}
      <div className="convo-composer-zone" data-eh-keyboard-zone>
        {composerDoor ?? (<>
        {composerLine && <p className="convo-composer-line">{composerLine}</p>}
        {about && (
          <span className="convo-about-chip">
            <span className="convo-about-chip-text">{about.label}</span>
            <button
              type="button"
              className="convo-about-chip-drop"
              aria-label="Send without it"
              data-log="talk_about_drop"
              {...convoLogAttrs}
              onClick={() => setAbout(null)}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </span>
        )}
        {/* Three things they could say, as chips over the field (2026-09-06
            走查: as bubbles in the thread they read as messages already sent).
            A tap sends the line as the first turn; the group unmounts with it
            (the parent stops passing starters once anything has been said). */}
        {starters && starters.length > 0 && !typing && !disabled && (
          <div className="convo-starters" role="group" aria-label="Ways to start">
            {starters.map((line, i) => (
              <button
                key={line}
                type="button"
                className="convo-starter"
                data-log="talk_starter_pick"
                data-log-position={i + 1}
                {...convoLogAttrs}
                onClick={() => { void onSend(line, []).then((ok) => { if (ok) sendsRef.current += 1; }); }}
              >
                {line}
              </button>
            ))}
          </div>
        )}
        {pending.length > 0 && (
          <ul className="convo-tray" aria-label="Photos to send">
            {pending.map((p, i) => (
              <li key={p.key} className={`convo-tray-item${p.uploading ? ' is-uploading' : ''}`}>
                <button
                  type="button"
                  className="convo-tray-preview"
                  data-log="photo_preview_open"
                  onClick={() => setPreview({ images: pending.map((x) => x.previewUrl), index: i })}
                  aria-label="Preview image"
                >
                  <img src={p.previewUrl} alt="Photo selected for sharing" />
                </button>
                {p.uploading && <span className="convo-tray-spinner" aria-label="Uploading" />}
                <button
                  type="button"
                  className="convo-tray-remove"
                  data-log="talk_photo_remove"
                  onClick={() => photos.onRemovePhoto(p.key)}
                  aria-label="Remove photo"
                >
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden>
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}
        {photos.fullLine && <p className="convo-photo-full" role="status">{photos.fullLine}</p>}
        {/* The field, and — while the one question after an Ember landed is
            up (2026-09-15 §4.5; 2026-09-21 redrawn) — the question as the
            field's own top: one shape, the field flat under it, the first
            keystroke into the field sends it away. */}
        <div className={`convo-field${helpAsk ? ' has-help' : ''}`}>
        {helpAsk && (
          <HelpAsk paused={helpAsk.paused} draftStarted={input.trim().length > 0} onClose={helpAsk.onClose} onDone={helpAsk.onDone} />
        )}
        {(recorder.recording || transcribing) ? (
          <div className={`convo-composer is-recording${transcribing ? ' is-transcribing' : ''}`}>
            <button
              type="button"
              className="convo-mic-cancel"
              data-log="talk_mic_cancel"
              {...convoLogAttrs}
              onClick={recorder.cancel}
              disabled={transcribing}
              aria-label="Cancel recording"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
            {transcribing || !recorder.analyser ? (
              <span className="convo-mic-pulse" aria-label="Turning speech into words">
                <span /><span /><span />
              </span>
            ) : (
              <VoiceWave analyser={recorder.analyser} />
            )}
            <button
              type="button"
              className="convo-mic-done"
              data-log="talk_mic_done"
              {...convoLogAttrs}
              onClick={() => { void finishRecording('field'); }}
              disabled={transcribing}
              title="Stop and read it first"
              aria-label="Stop recording and put the words in the field"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden>
                <rect x="6" y="6" width="12" height="12" rx="2.5" />
              </svg>
            </button>
            <button
              type="button"
              className="convo-mic-send"
              data-log="talk_mic_send"
              {...convoLogAttrs}
              onClick={() => { void finishRecording('send'); }}
              disabled={transcribing || typing || uploading}
              title="Stop and send"
              aria-label="Stop recording and send"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 19V6" />
                <path d="M6 12l6-6 6 6" />
              </svg>
            </button>
          </div>
        ) : (
        <div className="convo-composer">
          {/* Inside the pill, leading the field (ChatGPT-app order): picking
              adds the photo to the pending turn. Uploading never sends by
              itself; the person may add words or send the ready photo on its
              own with the arrow. */}
          <input
            ref={fileRef}
            type="file"
            className="convo-file-input"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            multiple
            tabIndex={-1}
            aria-hidden
            onChange={onFilesPicked}
          />
          <button
            type="button"
            className="convo-attach-btn"
            data-log="talk_photo_add"
            {...convoLogAttrs}
            onClick={() => {
              // The picker opens inside the tap (a browser opens a file picker only from the gesture itself);
              // the room left is re-read meanwhile, and the batch is trimmed to it when it comes back.
              photos.onPickerOpen?.();
              fileRef.current?.click();
            }}
            disabled={disabled || photos.remaining <= 0}
            title="Add a photo"
            aria-label="Add a photo"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
          <textarea
            ref={inputRef}
            className="convo-input"
            data-log-seen="talk_composer"
            {...convoLogAttrs}
            placeholder={composerPlaceholder ?? 'Start anywhere…'}
            value={input}
            rows={1}
            dir="auto"
            disabled={disabled}
            onChange={onInputChange}
            onKeyDown={onKeyDown}
            onFocus={() => {
              if (firstFocusRef.current === null) firstFocusRef.current = Math.round(performance.now());
            }}
            aria-label="Your message"
          />
          <button
            type="button"
            className="convo-mic-btn"
            data-log="talk_mic_start"
            {...convoLogAttrs}
            onClick={() => { void startRecording(); }}
            disabled={disabled}
            title="Say it instead"
            aria-label="Record a voice message"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="9" y="3" width="6" height="11" rx="3" />
              <path d="M6 11a6 6 0 0 0 12 0M12 17v4" />
            </svg>
          </button>
          {typing ? (
            <button
              key="stop"
              type="button"
              className="convo-stop-btn"
              onClick={() => { if (stopArmed) onStop(); }}
              data-log="talk_stop"
              data-log-state={stopArmed ? undefined : 'held'}
              {...convoLogAttrs}
            >
              Stop
            </button>
          ) : (
            <button
              key="send"
              type="button"
              className="convo-send-btn"
              onPointerDown={keepFieldFocus}
              onClick={() => { void send(); }}
              disabled={!canSend}
              aria-label="Send"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 19V6" />
                <path d="M6 12l6-6 6 6" />
              </svg>
            </button>
          )}
        </div>
        )}
        </div>
        </>)}
        {/* What Ember is, said once and left standing (2026-09-17 二次拍板). */}
        <p className="convo-ai-line">Ember is an AI. It can be wrong.</p>
      </div>
      {preview && (
        <ImagePreview
          src={preview.images[preview.index]}
          images={preview.images}
          index={preview.index}
          alt="Shared photo"
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

/**
 * An Ember someone let this person read (§4.9, 2026-09-27), given in this
 * conversation: a compact card after the turn that gave it — the cover, the
 * kicker, the title, one door to /read/:id where the whole Ember and the
 * notes are.
 */
function ReadCard({ read, logAttrs }: { read: ConversationReadItem; logAttrs: { 'data-log-conversation-id'?: string } }): ReactElement {
  return (
    <article
      className="convo-ember convo-read"
      data-read-id={read.id}
      data-log-seen="read_card"
      data-log-read-id={read.id}
      data-log-ember-id={read.ember_id}
      {...logAttrs}
    >
      <button
        type="button"
        className="convo-ember-open"
        data-log="read_card_open"
        data-log-read-id={read.id}
        data-log-ember-id={read.ember_id}
        onClick={() => navigate(`/read/${encodeURIComponent(read.id)}`)}
      >
        {read.cover && (
          <span className="convo-ember-cover">
            <img src={read.cover} alt="" loading="lazy" decoding="async" onError={(e) => { e.currentTarget.parentElement?.remove(); }} />
          </span>
        )}
        <span className="convo-ember-body">
          <span className="convo-ember-kicker">An Ember someone let you read</span>
          <span className="convo-ember-title" dir="auto">{read.title || 'An Ember'}</span>
          {read.value && <span className="convo-ember-lede" dir="auto">{read.value}</span>}
          <span className="convo-ember-read">
            <span>{read.opened_at ? 'Read it again' : 'Read it'}</span>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 6l6 6-6 6" />
            </svg>
          </span>
        </span>
      </button>
    </article>
  );
}

/**
 * How it went for others (§4.22, 2026-10-05): the page Ember put in front of
 * this person under the reply that offered it — the same paper card as a
 * read, its title the uncertainty in Ember's words, one door to the page.
 */
function PrecedentCard({ page, logAttrs }: { page: ConversationPrecedentItem; logAttrs: { 'data-log-conversation-id'?: string } }): ReactElement {
  return (
    <article className="convo-ember convo-read convo-precedent" data-precedent-id={page.id} {...logAttrs}>
      <button
        type="button"
        className="convo-ember-open"
        data-log="precedent_card_open"
        data-log-precedent-id={page.id}
        onClick={() => navigate(`/how-it-went/${encodeURIComponent(page.id)}`)}
      >
        <span className="convo-ember-body">
          <span className="convo-ember-kicker">How it went for others</span>
          <span className="convo-ember-title" dir="auto">{page.title}</span>
          {page.lede && <span className="convo-ember-lede" dir="auto">{page.lede}</span>}
          <span className="convo-ember-read">
            <span>{page.opened_at ? 'See it again' : 'See it'}</span>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 6l6 6-6 6" />
            </svg>
          </span>
        </span>
      </button>
    </article>
  );
}

/**
 * What a guest said yes to, waiting on their joining (2026-10-11 拍板; mock join-to-receive): a shared Ember or a room
 * passes between people who have joined, so it is given the moment they join — the card says why, and its button is
 * the one registration (one text, or Google in the same sheet). After they join, a room's card says Ember is looking;
 * a shared Ember's is replaced by the Ember itself (ReadCard). Fixed words, the same for everyone.
 */
const JOIN_WORDS = {
  shared_ember: {
    title: 'Join to read this story',
    body: 'Shared stories pass only between people who\u2019ve joined Ember. That way you can leave a note for the person who lived it, and they can write back.',
  },
  room: {
    title: 'Join so I can start looking',
    body: 'A room is a few people who\u2019ve joined Ember, talking with each other over a few days. Joining is also how I\u2019ll let you know when your group is ready.',
  },
} as const;

/**
 * A card for meeting people under the reply that brought it (2026-10-26 founder, New friends): the reply mentions it,
 * the card is the way in — a new friend, rooms by topic, or one room Ember invited them into (straight in from its page).
 */
const FEATURE_WORDS = {
  friends: { kicker: 'who could it be?', title: 'Want someone new to chat with?', body: 'Big stuff, small stuff, what\u2019s for dinner.', cta: 'Take a look', note: '3 quick taps' },
  rooms: { kicker: 'a room?', title: 'A room about what\u2019s on your mind?', body: '3 to 8 people on the same thing, plus me.', cta: 'Take a look', note: '2 quick taps' },
} as const;

function FeatureCard({ card, logAttrs }: { card: ConversationFeatureCard; logAttrs: { 'data-log-conversation-id'?: string } }): ReactElement | null {
  if (card.what === 'group' || card.what === 'group_post') return card.gone || !card.group_id ? null : <GroupCard card={card} logAttrs={logAttrs} />;
  if (card.what === 'room') {
    const gone = card.status === 'gone' || !card.room_id;
    const present = card.present ?? 0;
    // A room they are in: theirs to open, full or closed (2026-10-27 walk-through).
    const sub = gone ? 'Not taking people right now'
      : card.mine ? (card.status === 'closed' ? 'Closed · still yours to read' : `You’re in · ${present} ${card.status === 'forming' ? 'waiting' : 'inside'}`)
        : card.status === 'forming' ? `${present} waiting · opens when 3 are in`
          : `${present} inside · room for ${card.space ?? 0} more`;
    return (
      <article className={`convo-feature is-room${gone ? ' is-gone' : ''}`} data-log-seen="talk_feature_card" data-log-state="room" data-log-match-id={card.room_id ?? undefined} {...logAttrs}>
        <div className="convo-feature-band is-room" aria-hidden>
          <span className="convo-feature-dots"><i /><i /><i /></span>
          <span className="convo-feature-kicker is-room">room for you!</span>
        </div>
        <div className="convo-feature-row">
          <span className="convo-feature-text">
            <span className="convo-feature-title">{card.topic || 'A room'}</span>
            <span className="convo-feature-body">{sub}</span>
          </span>
          {gone ? null : (
            <Link to={card.mine ? `/rooms/${encodeURIComponent(card.room_id ?? '')}` : `/friends/rooms/${encodeURIComponent(card.room_id ?? '')}?invited=1`} className="convo-feature-btn is-room" data-log="talk_feature_card_open" data-log-state="room" data-log-match-id={card.room_id ?? undefined}>
              {card.mine ? 'Open' : 'Peek inside'}
            </Link>
          )}
        </div>
      </article>
    );
  }
  // A kind of card this build has no words for (one the server learned after it shipped) is not drawn: reading its
  // words off undefined took the whole conversation down (release check 2026-10-27). hasOwnProperty, not Object.hasOwn
  // (iOS Safari before 15.4 has none).
  if (!Object.prototype.hasOwnProperty.call(FEATURE_WORDS, card.what)) return null;
  const words = FEATURE_WORDS[card.what];
  return (
    <article className={`convo-feature is-${card.what}`} data-log-seen="talk_feature_card" data-log-state={card.what} {...logAttrs}>
      <div className={`convo-feature-band is-${card.what}`} aria-hidden>
        <span className="convo-feature-flame">{FLAME}</span>
        <span className="convo-feature-kicker">{words.kicker}</span>
      </div>
      <div className="convo-feature-copy">
        <h3 className="convo-feature-title">{words.title}</h3>
        <p className="convo-feature-body">{words.body}</p>
      </div>
      <div className="convo-feature-row">
        <Link to={card.what === 'friends' ? '/friends' : '/friends/rooms/new'} className="convo-feature-btn" data-log="talk_feature_card_open" data-log-state={card.what}>
          {words.cta}
        </Link>
        <span className="convo-feature-note">{words.note}</span>
      </div>
    </article>
  );
}

/**
 * A group's card under the reply that brought it (Groups 2026-11-03; mocks ChatOffer / ChatAsk), light on words:
 * `group` — Ember wrote up a post for them in the group; it opens filled in, to look over and post.
 * `group_post` — Ember asked them to answer one post: who asks (or shares) and the question; once answered, theirs to open.
 */
function GroupCard({ card, logAttrs }: { card: ConversationFeatureCard; logAttrs: { 'data-log-conversation-id'?: string } }): ReactElement {
  const groupId = card.group_id ?? '';
  if (card.what === 'group') {
    return (
      <article className="convo-group" data-log-seen="talk_feature_card" data-log-state="group" data-log-match-id={groupId} {...logAttrs}>
        <span className="convo-group-kicker">wrote it up for you</span>
        <h3 className="convo-group-title" dir="auto">{card.name || 'Your group'}</h3>
        <span className="convo-group-note">
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden><rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="2" fill="none" /><path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2" fill="none" /></svg>
          No names. Change anything before it goes up.
        </span>
        <Link to={`/groups/${encodeURIComponent(groupId)}/new?draft=1`} className="convo-group-btn" data-log="talk_feature_card_open" data-log-state="group" data-log-match-id={groupId}>
          Look &amp; post
        </Link>
      </article>
    );
  }
  const postId = card.post_id ?? '';
  const share = card.kind === 'share';
  const author = card.author;
  return (
    <article className="convo-group" data-log-seen="talk_feature_card" data-log-state="group_post" data-log-match-id={postId} {...logAttrs}>
      <span className="convo-group-who">
        {author && (author.ember
          ? <span className="convo-group-face is-ember" aria-hidden>{FLAME}</span>
          : <span className="convo-group-face" aria-hidden><PresenceAvatar avatarKey={author.avatar || ''} size={26} /></span>)}
        <span><strong dir="auto">{author?.name || 'Someone'}</strong> {share ? 'shares' : 'asks'}</span>
      </span>
      <p className="convo-group-title" dir="auto">{share ? card.excerpt : card.question || card.excerpt}</p>
      {card.answered ? (
        <span className="convo-group-row">
          <span className="convo-group-done">You answered</span>
          <Link to={`/groups/p/${encodeURIComponent(postId)}`} className="convo-group-btn is-soft" data-log="talk_feature_card_open" data-log-state="group_post" data-log-match-id={postId}>Open</Link>
        </span>
      ) : (
        <Link to={`/groups/p/${encodeURIComponent(postId)}?answer=1`} className="convo-group-btn" data-log="talk_feature_card_open" data-log-state="group_post" data-log-match-id={postId}>
          {share ? 'Reply' : 'Answer'}
        </Link>
      )}
    </article>
  );
}

const FLAME = (
  <svg viewBox="0 0 24 24" width="30" height="30" aria-hidden>
    <path d="M12 2.5c1.2 3.2 5.5 5.6 5.5 10.6A5.5 5.5 0 0 1 6.5 13c0-2.3 1.1-3.7 2.3-5 .3 1.6 1.1 2.7 2.2 3.2C10.6 8.6 10.9 5.2 12 2.5z" fill="#F4720C" />
    <path d="M12 12.2c.8 1.4 2.4 2.3 2.4 4.2a2.4 2.4 0 0 1-4.8 0c0-1.5 1-2.6 2.4-4.2z" fill="#FFB010" />
  </svg>
);

function JoinCard({ card }: { card: ConversationJoinCard }): ReactElement | null {
  if (card.state === 'looking') {
    return (
      <article className="convo-join is-looking">
        <span className="convo-join-kicker is-done">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
          You&rsquo;re in
        </span>
        <h3 className="convo-join-title">I&rsquo;m looking</h3>
        <p className="convo-join-body">I&rsquo;ll let you know when a few people are ready. If you&rsquo;d rather I stop, just tell me.</p>
      </article>
    );
  }
  // The same for a join card of a kind this build does not know (release check 2026-10-27).
  if (!Object.prototype.hasOwnProperty.call(JOIN_WORDS, card.what)) return null;
  const words = JOIN_WORDS[card.what];
  return (
    <article className="convo-join">
      <span className="convo-join-kicker">One step first</span>
      <h3 className="convo-join-title">{words.title}</h3>
      <p className="convo-join-body">{words.body}</p>
      <InboundRegistrationCTA
        logName="talk_join_to_receive"
        discovery={card.what}
        seen
        label="Join with one text"
        icon={<svg className="convo-join-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden><rect x="7" y="2.5" width="10" height="19" rx="2.5" /><path d="M11 18h2" /></svg>}
        className="ember-btn ember-btn-primary convo-join-btn"
      />
      <span className="convo-join-fine">Your conversation comes with you.</span>
    </article>
  );
}

/**
 * An Ember in the thread (2026-09-12): the page Ember wrote while the person
 * was away, placed where the telling it came from ends — its cover, its
 * title, its first lines, one door to read the whole page in place. It is
 * drawn only once whole and presented (the arrival ceremony is what brings it
 * in; `arriving` is that ceremony's stage for this card).
 */
function EmberCard({ ember, note, logAttrs, arriving, onOpen }: {
  ember: ConversationEmber;
  note?: ReactNode;
  logAttrs: { 'data-log-conversation-id'?: string };
  arriving: 'ceremony' | 'landed' | null;
  onOpen?: (ember: ConversationEmber) => void;
}): ReactElement {
  const cover = ember.media.header_image_url;
  const lede = ember.canonical_text.split(/\n+/).find((line) => line.trim())?.trim() ?? '';
  return (
    <article
      className={`convo-ember${arriving === 'ceremony' ? ' is-slot' : ''}${arriving === 'landed' ? ' is-landed' : ''}`}
      data-ember-id={ember.id}
      data-log-seen={arriving === 'ceremony' ? undefined : 'talk_ember_card'}
      data-log-ember-id={ember.id}
      {...logAttrs}
    >
      <button
        type="button"
        className="convo-ember-open"
        data-log="talk_ember_open"
        data-log-state="open"
        data-log-ember-id={ember.id}
        onClick={() => onOpen?.(ember)}
      >
        {cover && (
          <span className="convo-ember-cover">
            {/* An arriving card loads its cover while it stands invisible, so
                the page lands on the picture, not on a blank that fills in. */}
            <img src={cover} alt="" loading={arriving ? 'eager' : 'lazy'} decoding="async" onError={(e) => { e.currentTarget.parentElement?.remove(); }} />
          </span>
        )}
        <span className="convo-ember-body">
          <span className="convo-ember-kicker">Your Ember</span>
          <span className="convo-ember-title" dir="auto">{ember.title || 'Untitled'}</span>
          {lede && <span className="convo-ember-lede" dir="auto">{lede}</span>}
          {/* The light it stands at (§4.17, 2026-09-24): how many people stand
              where this person stands. The card is one button, so this is a
              line of it — the light's own page is a tap away from the Ember. */}
          {ember.lights[0] && (
            <span className="convo-ember-light"><PeopleLine count={ember.lights[0].people_count} /></span>
          )}
          <span className="convo-ember-read">
            <span>Read the page</span>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 6l6 6-6 6" />
            </svg>
          </span>
        </span>
      </button>
      {note && <div className="convo-ember-note">{note}</div>}
    </article>
  );
}
